import PocketBase from 'pocketbase'

export const pb = new PocketBase('https://gwiz-api.yvt.io')

// Enable auto-cancellation for duplicate requests
pb.autoCancellation(false)

// Type definitions for PocketBase collections
export interface User {
  id: string
  email: string
  name: string
  avatar?: string
  role: 'worker' | 'analyst' | 'viewer' | 'ocr'
  created: string
  updated: string
}

export interface Agency {
  id: string
  name: string
  name_th?: string
  name_en?: string
  dept_code?: string
  ministry?: string
  province?: string
  province_code?: string
  address?: string
  phone?: string
  website?: string
  created: string
  updated: string
}

export interface Company {
  id: string
  name: string
  name_th?: string
  name_en?: string
  tax_id?: string
  address?: string
  phone?: string
  website?: string
  created: string
  updated: string
}

export interface Tender {
  id: string
  project_id?: string
  project_name?: string
  egp_project_no?: string
  source_url?: string
  title_th?: string
  procurement_method?: string
  procurement_type?: string
  procurement_item?: string
  plan_ref?: string
  agency?: string
  agency_name?: string
  agency_phone?: string
  location_text?: string
  tags?: string[]
  province_code?: string
  budget_year?: number
  project_status?: ProjectStatus
  created: string
  updated: string
  expand?: {
    agency?: Agency
    tags?: Tag[]
  }
}

export interface TenderVersion {
  id: string
  tender_id: string
  version_number: number
  project_id?: string
  project_name?: string
  dept_name?: string
  dept_sub_name?: string
  ministry?: string
  dept_code?: string
  province_code?: string
  procurement_method?: string
  procurement_type?: string
  project_status?: ProjectStatus
  budget_year?: number
  sum_price_agree?: number
  price_build?: number
  published_at?: string
  effective_at?: string
  transaction_id?: string
  raw_json?: Record<string, unknown>
  created: string
  updated: string
}

export interface TenderStatusEvent {
  id: string
  tender_id: string
  status?: string
  occurred_at?: string
  note?: string
  created: string
  updated: string
}

export interface Attachment {
  id: string
  tender_id?: string
  tender_version?: string
  file: string
  title?: string
  role?: AttachmentRole
  published_at?: string
  downloaded_at?: string
  source_url?: string
  sha256?: string
  size_bytes?: number
  ocr_status?: OcrStatus
  ocr_file?: string
  type?: AttachmentType
  bundle_key?: string
  created: string
  updated: string
}

export interface Award {
  id: string
  tender_id: string
  tender_version?: string
  company_id?: string
  award_date?: string
  total_price?: number
  currency?: 'THB'
  details_json?: Record<string, unknown>
  created: string
  updated: string
  expand?: {
    company_id?: Company
    tender_id?: Tender
  }
}

export interface Bid {
  id: string
  tender_id: string
  company_id?: string
  bid_price?: number
  submitted_at?: string
  is_winner?: boolean
  details_json?: Record<string, unknown>
  created: string
  updated: string
  expand?: {
    company_id?: Company
    tender_id?: Tender
  }
}

export interface Contract {
  id: string
  tender_id: string
  company_id?: string
  contract_number?: string
  sign_date?: string
  planned_end_date?: string
  actual_end_date?: string
  total_price?: number
  currency?: 'THB'
  status?: string
  details_json?: Record<string, unknown>
  created: string
  updated: string
  expand?: {
    company_id?: Company
    tender_id?: Tender
  }
}

export interface Tag {
  id: string
  name: string
  name_th?: string
  name_en?: string
  slug?: string
  parent?: string
  depth?: number
  tender_count?: number
  created: string
  updated: string
}

export interface RfqForm {
  id: string
  tender_id: string
  tender?: string
  source_attachments?: string[]
  request_no?: string
  request_date?: string
  project_name?: string
  budget_amount_satang?: number
  ref_amount_satang?: number
  budget_amount?: number
  ref_amount?: number
  submission_window_start?: string
  submission_window_end?: string
  price_hold_days?: number
  delivery_days_after_sign?: number
  delivery_location?: string
  sample_qty?: number
  sample_due_at?: string
  contract_sign_days?: number
  performance_bond_percent?: number
  ld_rate_percent_per_day?: number
  warranty_months?: number
  additional_terms?: string
  status?: 'draft' | 'model_filled' | 'human_reviewed' | 'published' | 'archived'
  total_json?: Record<string, unknown>
  total_amount?: number
  line_count?: number
  filled_pdf?: string
  llm_model?: string
  llm_raw_json?: Record<string, unknown>
  created: string
  updated: string
  expand?: {
    tender_id?: Tender
  }
}

export interface RfqFormItem {
  id: string
  rfq_form?: string
  form_id: string
  line_number?: number
  description?: string
  item_code?: string
  qty?: number
  quantity?: number
  unit?: string
  unit_price_satang?: number
  unit_price?: number
  vat_rate_percent?: number
  line_total_satang?: number
  line_total?: number
  specifications?: string
  created: string
  updated: string
}

export interface CrawlRun {
  id: string
  source?: string
  started_at: string
  finished_at?: string
  status: 'queued' | 'running' | 'completed' | 'failed'
  tenders_created?: number
  tenders_updated?: number
  error_message?: string
  created: string
  updated: string
}

export interface OcrTask {
  id: string
  attachment_id: string
  status: OcrStatus
  engine?: string
  error_message?: string
  created: string
  updated: string
  expand?: {
    attachment_id?: Attachment
  }
}

export interface OcrResult {
  id: string
  attachment_id: string
  engine?: string
  version?: string
  extracted_text?: string
  page_count?: number
  confidence?: number
  text_json?: Record<string, unknown>
  created: string
  updated: string
  expand?: {
    attachment_id?: Attachment
  }
}

export interface UserBookmarkedAgency {
  id: string
  user_id: string
  agency_id: string
  note?: string
  pinned?: boolean
  created: string
  updated: string
  expand?: {
    agency_id?: Agency
  }
}

export interface UserBookmarkedTender {
  id: string
  user_id: string
  tender_id: string
  note?: string
  pinned?: boolean
  created: string
  updated: string
  expand?: {
    tender_id?: Tender
  }
}

export interface UserBookmarkedTag {
  id: string
  user_id: string
  tag_id: string
  note?: string
  pinned?: boolean
  created: string
  updated: string
  expand?: {
    tag_id?: Tag
  }
}

// View types for combined queries
export interface VTendersPublic {
  id: string
  tender?: string
  tender_id: string
  egp_project_no?: string
  title_th?: string
  project_id?: string
  project_name?: string
  agency?: string
  dept_name?: string
  dept_sub_name?: string
  province_code?: string
  procurement_method?: string
  procurement_type?: string
  procurement_item?: string
  project_status?: ProjectStatus
  budget_amount_satang?: number
  median_price_satang?: number
  procured_amount_satang?: number
  currency?: string
  sum_price_agree?: number
  price_build?: number
  published_at?: string
  budget_year?: number
  version_number?: number
  created: string
  updated: string
}

export interface VTenderLatestVersion extends TenderVersion {}

export interface TenderSearchItem {
  id: string
  tender_id: string
  egp_project_no?: string
  project_name?: string
  title_th?: string
  dept_name?: string
  province_code?: string
  procurement_method?: string
  project_status?: ProjectStatus
  budget_amount_satang?: number
  procured_amount_satang?: number
  sum_price_agree?: number
  published_at?: string
}

export interface TenderSearchStats {
  totalCount: number
  totalBudget: number
  biddingCount: number
}

export interface TenderSearchResponse {
  page: number
  perPage: number
  totalItems: number
  totalPages: number
  items: TenderSearchItem[]
  stats: TenderSearchStats
}

// Enum types - using eGP schema keys
export type ProjectStatus =
  | 'tor-prep'
  | 'purchase-request'
  | 'invitation-issued'
  | 'award-approved'
  | 'contract-management'
  | 'project-cancelled'

export type OcrStatus = 'pending' | 'processing' | 'done' | 'failed'

export type AttachmentType =
  | 'median-price-announcement'
  | 'tor-draft-announcement'
  | 'invitation-announcement'
  | 'winner-announcement'
  | 'contract-essentials'
  | 'winner-change-notice'
  | 'invitation-cancel'
  | 'winner-cancel'
  | 'invitation-combo'
  | 'winner-combo'
  | 'initial-bid-summary'
  | 'quarterly-award-report'

export type AttachmentRole =
  | 'invitation-notice'
  | 'bidding-doc'
  | 'tor-draft'
  | 'median-price'
  | 'contract-essentials'
  | 'change-notice'
  | 'cancel-notice'
  | 'combo'
  | 'initial-bid-summary'
  | 'quarterly-report'
  | 'supporting'
  | 'screenshot'
  | 'other'
